// Tiers Sage (BS-1765, intégration custom Groupe Bertrand) : lecture de l'état
// de synchro d'une fiche et appels des callables. Une fiche est jamais envoyée,
// synchronisée, ou en erreur (dernier envoi ou renvoi automatique en échec :
// bloque la facturation même si la fiche a déjà un identifiant Sage).
//
// Mise à jour automatique (08/10/2026) : l'enregistrement d'une fiche déjà
// connectée passe par `sage_saveTiers` (« Sage d'abord, enregistrement
// ensuite »), voir `getSageSaveVenue` et `saveTiersToSage`.
import { getCallableFunctionV2 } from '../backend'

export const SAGE_CONNECTOR_ID = 'sage'

/** L'intégration est active sur cette venue : listée dans customIntegrations ET connecteur complet activé. */
export const isSageEnabledOnVenue = venue => {
	if (!venue) return false
	const listed = Array.isArray(venue.customIntegrations) && venue.customIntegrations.includes(SAGE_CONNECTOR_ID)
	if (!listed) return false
	const connector = (venue.connect || []).find(c => c && c.id === SAGE_CONNECTOR_ID)
	return Boolean(connector && connector.enabled !== false && connector.apiKey && connector.baseUrl)
}

/**
 * Venue à utiliser pour envoyer une fiche : la venue de la fiche si elle porte
 * Sage, sinon (base de comptes de groupe) la première venue du périmètre qui
 * le porte. `null` quand l'intégration n'est active nulle part.
 */
export const getSageVenue = (venues, entity) => {
	const list = Array.isArray(venues) ? venues : []
	if (entity?.etablissement) {
		const own = list.find(v => v.id === entity.etablissement)
		if (own && isSageEnabledOnVenue(own)) return own
		// Fiche de groupe (les docs gardent leur `etablissement` d'origine) : n'importe
		// quelle venue Sage du groupe convient, le serveur accepte le périmètre groupe.
		return entity.venuesGroupId ? list.find(isSageEnabledOnVenue) || null : null
	}
	return list.find(isSageEnabledOnVenue) || null
}

/** La fiche a déjà un tiers côté Sage. */
export const hasSageId = entity => {
	const sageId = entity?.metadata?.sage_id
	return typeof sageId === 'string' && sageId.trim().length > 0
}

/**
 * `never` | `synced` | `error`. L'erreur prime : une fiche connectée dont le
 * dernier envoi ou renvoi a échoué est bloquée jusqu'à un renvoi réussi.
 */
export const getSageSyncState = entity => {
	if (entity?.sageSync?.lastError) return 'error'
	return hasSageId(entity) ? 'synced' : 'never'
}

/**
 * Venue par laquelle enregistrer une fiche déjà connectée (« Sage d'abord »),
 * ou `null` : fiche jamais envoyée, ou Sage inactif sur son périmètre. Le
 * périmètre est celui des fiches : toutes les venues de l'utilisateur en groupe,
 * sinon la venue de la fiche seule.
 */
export const getSageSaveVenue = ({ venues, venuesGroup, entity }) => {
	if (!entity || !hasSageId(entity)) return null
	const list = Array.isArray(venues) ? venues : []
	const target = venuesGroup?.id ? list : list.filter(v => v.id === entity.etablissement)
	return getSageVenue(target, entity)
}

/** Envoi vers Sage ; renvoie `{ success, sageId, syncedAt }`, lève l'HttpsError du callable. */
export const syncTiersToSage = async ({ venueId, accountId, clientId }) => {
	const call = getCallableFunctionV2('sage_syncTiers')
	const { data } = await call({ venueId, ...(accountId ? { accountId } : { clientId }) })
	return data
}

/**
 * Enregistrement « Sage d'abord » d'une fiche connectée : le serveur met le
 * tiers à jour si un champ envoyé à Sage change, puis écrit `data` ; un refus
 * de Sage lève l'HttpsError et rien n'est écrit. Renvoie `{ resent, sageId?, syncedAt? }`.
 */
export const saveTiersToSage = async ({ venueId, accountId, clientId, data }) => {
	const call = getCallableFunctionV2('sage_saveTiers')
	const { data: result } = await call({ venueId, ...(accountId ? { accountId } : { clientId }), data })
	return result
}

/**
 * Forme sérialisable d'une erreur de sauvegarde pour `rejectWithValue` :
 * `{ code, params, message, uncertain }`. Un callable porte son code métier dans
 * `details.code` ; une erreur Firestore ou Firebase brute garde son `code` technique.
 */
export const toSageRejection = error => {
	const details = error?.details
	if (details?.code) {
		return { code: details.code, params: details.params || {}, message: error.message || '', uncertain: Boolean(details.uncertain) }
	}
	return { code: error?.code || 'UNKNOWN', params: {}, message: error?.message || '' }
}

/**
 * Message utilisateur d'une erreur du callable (`error.details.code`) ou d'un
 * `sageSync.lastError` persisté ; `t` = i18n.
 */
export const getSageErrorMessage = (error, t) => {
	// HttpsError client : `details` = { code, params }, le message lisible est dans
	// `error.message`. Erreur persistée : `{ code, message, params }` à plat.
	const details = error?.details || error || {}
	const code = details.code
	const params = details.params || {}
	const reason = details.message || (error?.details ? error.message : '')
	switch (code) {
		case 'SAGE_MISSING_FIELDS': {
			const fields = (params.missing || []).map(f => t(`sage_field_${f}`)).join(', ')
			return params.countryLabel
				? `${t('sage_missing_fields')} : ${fields} (${t('country_not_recognized')} : « ${params.countryLabel} »)`
				: `${t('sage_missing_fields')} : ${fields}`
		}
		case 'SAGE_REJECTED': {
			// Champs refusés par leur API : traduits (clé inconnue affichée telle quelle)
			if (Array.isArray(params.fields) && params.fields.length > 0) {
				return `${t('sage_rejected')} : ${t('sage_missing_fields')} : ${params.fields.map(f => t(`sage_field_${f}`, { defaultValue: f })).join(', ')}`
			}
			return `${t('sage_rejected')}${reason ? ` : ${reason}` : ''}`
		}
		case 'SAGE_AUTH':
			return t('sage_auth_error')
		case 'SAGE_TIMEOUT':
			return t('sage_timeout_uncertain')
		case 'SAGE_UNAVAILABLE':
			return t('sage_unavailable')
		case 'SAGE_NOT_CONFIGURED':
			return t('sage_not_configured')
		case 'SAGE_STAFF_FORBIDDEN':
			return t('no_admin_rights')
		default:
			// Erreur Firebase brute (réseau, internal…) : jamais son texte technique.
			return reason && !String(error?.code || '').startsWith('functions/') ? reason : t('sage_unavailable')
	}
}

/**
 * Message d'une sauvegarde refusée (rejet du thunk ou erreur attrapée) : une
 * erreur Sage est expliquée et annonce que la fiche n'est pas enregistrée ; une
 * erreur Firestore ordinaire garde le message générique.
 */
export const getSageSaveErrorMessage = (rejection, t) => {
	const code = String(rejection?.code || '')
	if (code.startsWith('SAGE_') || code.startsWith('functions/')) {
		return t('sage_save_rejected', { reason: getSageErrorMessage(rejection, t) })
	}
	return t('error')
}
